'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useCopilot } from './context.js';
import { useDecideAction } from '../hooks/index.js';
import { CopilotActionItem } from './action-item.js';
// COP-135 D1 (task-08) — the step outcomes come from the turn-action ledger,
// joined by the step's own index. The card derives none of them.
import { planStepActionId, turnActionOutcomeLabel, } from './turn-actions.js';
function stringifyJsonSafe(value) {
    const seen = new WeakSet();
    try {
        const json = JSON.stringify(value, (_key, entry) => {
            if (typeof entry === 'bigint')
                return entry.toString();
            if (typeof entry === 'function')
                return '[Function]';
            if (typeof entry === 'symbol')
                return String(entry);
            if (entry && typeof entry === 'object') {
                if (seen.has(entry))
                    return '[Circular]';
                seen.add(entry);
            }
            return entry;
        }, 2);
        return json ?? String(value);
    }
    catch {
        return String(value);
    }
}
export function planErrorCopy(code) {
    if (code === 'hash_mismatch') {
        return 'This plan no longer matches the server record. Ask the assistant to propose it again.';
    }
    if (code === 'already_decided') {
        return 'This plan has already been decided.';
    }
    if (code === 'schedule_deleted') {
        return 'The schedule was deleted, so this plan cannot run.';
    }
    return 'Could not submit the plan decision. Please retry or ask the assistant to propose a fresh plan.';
}
/**
 * COP-119 §5 — render a step's declared step-output references as prose naming
 * the producing step. The persisted argument holds a placeholder ("[from step
 * 1]") because the real value does not exist until the plan runs; a user cannot
 * meaningfully approve that string, so the card says what it means instead.
 *
 * Pure and exported so it can be pinned without jsdom (see plan-card.test.ts).
 */
export function planStepRefsCopy(refs) {
    if (refs === undefined || refs.length === 0)
        return null;
    const clauses = refs.map((ref) => `the result of step ${ref.fromStep} for "${ref.path}"`);
    const joined = clauses.length === 1
        ? clauses[0]
        : `${clauses.slice(0, -1).join(', ')} and ${clauses[clauses.length - 1]}`;
    return `Uses ${joined}.`;
}
/**
 * COP-135 D1 (task-08) — the pre-execution banner, owned by the CARD.
 *
 * It used to be `plan.copy.riskNotice`, a string the planner wrote into the
 * stored preview before anything ran. The preview is a photo taken at proposal
 * time (`hub/src/chat/persistence.ts` stores it inline on purpose), so an
 * approved AND executed plan kept rendering "No write has been executed yet"
 * after every one of its writes had landed. The banner is a STATEMENT ABOUT
 * STATE, so it is derived from state: shown while no step has run, gone the
 * moment one has.
 *
 * The second sentence the stored notice carried ("Approving the plan continues
 * in a separate step.") is dropped rather than moved: it is false on an
 * already-approved plan, which is exactly the state this banner must survive.
 */
const PRE_EXECUTION_BANNER = 'No write has been executed yet.';
/**
 * The outcomes that mean a step's write was ATTEMPTED. `awaiting_approval`,
 * `denied` and `not_run` are deliberately absent: in all three nothing was
 * written, so the pre-execution banner is still true.
 *
 * `unknown` IS attempted (COP-138) — the call was dispatched and only its
 * answer was lost. Leaving it out would keep "No write has been executed yet."
 * on screen for a step that may well have written, which is the same confident
 * wrong answer in the banner rather than in the row.
 *
 * `awaiting_job` IS attempted too (COP-156 D6, PR #319 review R9): the track
 * answered the dispatched write with 202 — it accepted the operation and may
 * already have written.
 */
const EXECUTION_ATTEMPTED = new Set([
    'running',
    'awaiting_job',
    'succeeded',
    'failed',
    'unknown',
]);
/**
 * Each step's outcome, in the preview's own step order, joined to the ledger by
 * the step index. A step with no row is `not_run` — never omitted, so the marker
 * list and the step list are always the same length.
 */
function stepOutcomes(plan, actions) {
    return plan.plan.steps.map((step) => actions?.get(planStepActionId(plan.plan.id, step.index))?.outcome ?? 'not_run');
}
/** The step outcomes that stop a plan: the row reads Failed, Denied, Edited (P78) or Unconfirmed. */
const STEP_STOPPED = new Set(['failed', 'denied', 'edited', 'unknown']);
/**
 * M44 gate O2 — the title and the closing line of a plan whose execution
 * ended. The failed step is the first step row that stopped the plan, so the
 * line agrees with the rows above it; with no such row it names no step.
 */
function executionEndCopy(execution, plan, outcomes) {
    if (execution === 'completed')
        return { title: 'Plan completed', footer: 'Plan completed. All steps ran.' };
    const position = outcomes.findIndex((outcome) => STEP_STOPPED.has(outcome));
    const step = position === -1 ? undefined : plan.plan.steps[position];
    // P84 — the owner cancelled the run (P82): say so, not "failed".
    const end = execution === 'cancelled' ? 'cancelled' : 'failed';
    const title = `Plan ${end}`;
    return { title, footer: step ? `${title} at step ${step.index}.` : `${title}.` };
}
/** A PlanPendingPart may optionally carry a light-touch read-summary caption
 * (PLN-18). The browser schema does not require it; render it only when present
 * (do not build new read-summary plumbing here). */
function readSummaryCaption(plan) {
    const raw = plan.plan.readSummary;
    return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
}
/**
 * Hash-bound decision card for a Hub `plan` proposal (COP-092/COP-093).
 *
 * The default UI renders only `PlanPendingPart` preview data: copy, expiry,
 * ordered step metadata, and each redacted `argsPreview`. The card offers three
 * mutually-exclusive controls, all hash-bound to the rendered plan's
 * `payloadHash` (INV-03) and posted through `useCopilot().hub.plansDecide(...)`:
 *
 *  - Execute plan — `approve` + `wholePlanApproved: true` + `consentScope:
 *    'session'`. The route mints ONE session-scoped plan-envelope consent grant
 *    bound to plan_id + payload_hash; the executor runs all N steps with no
 *    per-step card (COP-057 blanket path re-expressed as a grant).
 *  - Execute step by step — `approve` with no `wholePlanApproved`; no envelope
 *    grant is created and each write hits the per-step consent seam.
 *  - Deny — `reject` (optional reason). The plan resolves rejected; nothing
 *    executes; no grant is created.
 *
 * No executable `steps`, raw `args`, or preview blobs are posted. After an
 * approve decision the card triggers execution only by sending the server-owned
 * `pendingPlanId` into the next chat continuation; Deny never sends it. Steps
 * are read-only — there is no per-step edit/delete/reorder (PLN-19).
 */
export function PlanCard({ plan, className, children, initialState, execution, actions, variant = 'default', }) {
    const { hub, sendMessage } = useCopilot();
    const [localState, setLocalState] = useState(initialState ?? 'pending');
    const [reasonText, setReasonText] = useState('');
    const [showDenyField, setShowDenyField] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const { submittedRef, applyDecideError } = useDecideAction({
        errorCopy: planErrorCopy,
        resolveError: (e, copy) => {
            if (e.code === 'plan_expired') {
                setLocalState('expired');
                setErrorText(null);
                return;
            }
            setLocalState('error');
            setErrorText(copy);
        },
    });
    // COP-092 PlanCard affordances; COP-093 Execute-plan=session envelope grant.
    const onExecutePlan = async () => {
        if (submittedRef.current)
            return;
        submittedRef.current = true;
        setLocalState('approving');
        setErrorText(null);
        try {
            await hub.plansDecide({
                params: { id: plan.plan.id },
                body: {
                    action: 'approve',
                    payloadHash: plan.plan.payloadHash,
                    wholePlanApproved: true,
                    consentScope: 'session',
                },
            });
            setLocalState('approved');
            sendMessage({ pendingPlanId: plan.plan.id });
        }
        catch (err) {
            applyDecideError(err);
        }
    };
    // COP-092 Execute step by step — approve plan, NO envelope grant; each write
    // surfaces its own consent card at execution time.
    const onExecuteStepByStep = async () => {
        if (submittedRef.current)
            return;
        submittedRef.current = true;
        setLocalState('approving');
        setErrorText(null);
        try {
            await hub.plansDecide({
                params: { id: plan.plan.id },
                body: {
                    action: 'approve',
                    payloadHash: plan.plan.payloadHash,
                },
            });
            setLocalState('approved');
            sendMessage({ pendingPlanId: plan.plan.id });
        }
        catch (err) {
            applyDecideError(err);
        }
    };
    const onDenyClick = () => {
        if (submittedRef.current)
            return;
        setShowDenyField(true);
    };
    const closeDenyField = () => setShowDenyField(false);
    // COP-092 Deny — reject; nothing executes; no grant. Optional reason flows back.
    const onDenySubmit = async () => {
        if (submittedRef.current)
            return;
        submittedRef.current = true;
        setLocalState('rejecting');
        setErrorText(null);
        const reason = reasonText.trim();
        try {
            await hub.plansDecide({
                params: { id: plan.plan.id },
                body: {
                    action: 'reject',
                    payloadHash: plan.plan.payloadHash,
                    ...(reason ? { reason } : {}),
                },
            });
            setLocalState('rejected');
        }
        catch (err) {
            applyDecideError(err);
        }
    };
    const renderProps = {
        plan,
        state: localState,
        ...(execution ? { execution } : {}),
        reasonText,
        setReasonText,
        errorText,
        onExecutePlan,
        onExecuteStepByStep,
        onDenyClick,
        onDenySubmit,
        showDenyField,
        closeDenyField,
    };
    if (children)
        return _jsx(_Fragment, { children: children(renderProps) });
    const caption = readSummaryCaption(plan);
    // COP-135 D1 — one outcome per step, from the ledger, by index. Nothing here
    // reads a part type or the card's local decision state.
    const outcomes = stepOutcomes(plan, actions);
    const executionAttempted = outcomes.some((outcome) => EXECUTION_ATTEMPTED.has(outcome));
    const compact = variant === 'compact';
    const ended = execution ? executionEndCopy(execution, plan, outcomes) : null;
    return (_jsxs("section", { className: [
            'copilot-plan-card',
            `copilot-plan-card-state-${localState}`,
            className,
        ]
            .filter(Boolean)
            .join(' '), "data-plan-card": "", "data-plan-id": plan.plan.id, "data-state": localState, "data-plan-execution": execution, role: "region", "aria-label": "Plan approval required", "data-variant": compact ? 'compact' : undefined, children: [_jsxs("header", { className: "copilot-plan-card-header", children: [_jsx("h3", { className: "copilot-plan-card-title", children: ended ? ended.title : plan.copy.title }), !ended && _jsx("p", { className: "copilot-plan-card-summary", children: plan.copy.summary }), !executionAttempted && (_jsx("p", { className: "copilot-plan-card-risk", role: "note", "data-plan-pre-execution": "", children: PRE_EXECUTION_BANNER })), !ended && _jsx("p", { className: "copilot-plan-card-expiry", children: plan.copy.expiryNotice })] }), executionAttempted && _jsx(PlanProgress, { outcomes: outcomes }), caption && (_jsx("p", { className: "copilot-plan-card-read-summary", role: "note", children: caption })), _jsx("ol", { className: "copilot-plan-card-steps", "aria-label": "Proposed plan steps", "data-step-count": plan.plan.steps.length, children: plan.plan.steps.map((step, position) => (
                // COP-153 — a step is drawn by the shared CopilotActionItem (status
                // circle with the step number, summary + outcome, tool + kind), the
                // same look the turn action rows use.
                _jsxs(CopilotActionItem, { as: "li", className: "copilot-plan-card-step", "data-step-index": step.index, "data-step-kind": step.kind, state: outcomes[position], marker: step.index, title: step.summary ? (
                    // COP-122 — the planner-authored line describing THIS step.
                    _jsx("span", { className: "copilot-plan-card-step-summary", children: step.summary })) : (_jsx("span", { className: "copilot-plan-card-step-tool", children: step.toolName })), outcome: 
                    // COP-135 D1 — what became of THIS step, from its ledger row.
                    // Always rendered: `not_run` is a statement, and a step that
                    // silently dropped its marker would read as an absent step.
                    _jsx("span", { className: "copilot-plan-card-step-outcome", "data-step-outcome": outcomes[position], children: turnActionOutcomeLabel(outcomes[position]) }), meta: _jsxs(_Fragment, { children: [_jsxs("span", { className: "copilot-plan-card-step-index", children: ["Step ", step.index] }), step.summary && _jsx("span", { className: "copilot-plan-card-step-tool", children: step.toolName }), step.kind === 'write' && (_jsx("span", { className: "copilot-plan-card-step-kind", "data-kind": "write", children: "write" }))] }), children: [_jsx("p", { className: "copilot-plan-card-step-reason", children: step.reason }), planStepRefsCopy(step.refs) && (
                        // COP-119 §5 — the argument below shows a placeholder; this line is
                        // what makes the step approvable.
                        _jsx("p", { className: "copilot-plan-card-step-refs", role: "note", children: planStepRefsCopy(step.refs) })), _jsxs("details", { className: "copilot-plan-card-step-details", children: [_jsx("summary", { className: "copilot-plan-card-step-details-toggle", children: "Details" }), _jsx(PlanStepArgs, { index: step.index, argsPreview: step.argsPreview })] })] }, step.index))) }), localState === 'pending' && !showDenyField && (
            // COP-092 — three mutually-exclusive controls. Sticky so they stay
            // reachable regardless of step count (PLN-17).
            _jsxs("div", { className: "copilot-plan-card-actions copilot-plan-card-actions-sticky", children: [_jsx("button", { type: "button", className: "copilot-plan-card-execute-plan", onClick: onExecutePlan, "aria-label": "Execute the whole plan", children: plan.plan.options.wholePlanApprovalLabel || 'Execute plan' }), _jsx("button", { type: "button", className: "copilot-plan-card-execute-step", onClick: onExecuteStepByStep, "aria-label": "Execute the plan step by step, approving each write", children: "Execute step by step" }), _jsx("button", { type: "button", className: "copilot-plan-card-deny", onClick: onDenyClick, "aria-label": "Deny this plan", children: "Deny" })] })), showDenyField && localState === 'pending' && (_jsxs("div", { className: "copilot-plan-card-reason", children: [_jsx("label", { htmlFor: `copilot-plan-deny-${plan.plan.id}`, children: "Why? (optional)" }), _jsx("textarea", { id: `copilot-plan-deny-${plan.plan.id}`, value: reasonText, onChange: (e) => setReasonText(e.target.value), maxLength: 2000 }), _jsxs("div", { className: "copilot-plan-card-reason-actions", children: [_jsx("button", { type: "button", className: "copilot-plan-card-deny", onClick: onDenySubmit, children: "Deny" }), _jsx("button", { type: "button", className: "copilot-plan-card-reason-cancel", onClick: closeDenyField, children: "Cancel" })] })] })), localState === 'approving' && (_jsx("div", { className: "copilot-plan-card-state-approving", role: "status", children: "Submitting plan approval\u2026" })), localState === 'approved' && (_jsx("div", { className: "copilot-plan-card-state-approved", role: "status", children: ended ? ended.footer : 'Plan approved. The server will continue to own step execution.' })), localState === 'rejecting' && (_jsx("div", { className: "copilot-plan-card-state-rejecting", role: "status", children: "Denying plan\u2026" })), localState === 'rejected' && (_jsx("div", { className: "copilot-plan-card-state-rejected", role: "status", children: "Plan denied. Nothing was executed." })), localState === 'expired' && (_jsx("div", { className: "copilot-plan-card-state-expired", role: "alert", children: "Plan expired. Ask the assistant to propose a fresh plan." })), localState === 'error' && (_jsx("div", { className: "copilot-plan-card-state-error", role: "alert", children: errorText ?? planErrorCopy(undefined) }))] }));
}
function PlanStepArgs({ index, argsPreview }) {
    return (_jsx("pre", { className: "copilot-plan-card-step-args-preview", "aria-label": `Step ${index} redacted arguments preview`, children: stringifyJsonSafe(argsPreview) }));
}
/**
 * COP-153 — the card's "2 of 3 done" line and bar. Counts only
 * `succeeded` rows from the ledger (COP-135 D1): a failed, denied or unconfirmed
 * step is not done, and the card does not guess.
 */
function PlanProgress({ outcomes }) {
    const total = outcomes.length;
    const done = outcomes.filter((outcome) => outcome === 'succeeded').length;
    const percent = total === 0 ? 0 : Math.round((done / total) * 100);
    return (_jsxs("div", { className: "copilot-plan-card-progress", children: [_jsxs("span", { className: "copilot-plan-card-progress-label", children: [done, " of ", total, " done"] }), _jsx("div", { className: "copilot-plan-card-progress-bar", role: "progressbar", "aria-label": "Plan progress", "aria-valuemin": 0, "aria-valuemax": total, "aria-valuenow": done, children: _jsx("span", { className: "copilot-plan-card-progress-fill", style: { width: `${percent}%` } }) })] }));
}
//# sourceMappingURL=plan-card.js.map